import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Spinner } from 'react-bootstrap';

import DepositInvoiceModal from '../components/DepositInvoiceModal';
import CreateEstimateModal from '../components/CreateEstimateModal';

interface Project {
  id: number;
  name: string;
  url?: string;     
  contactUrl?: string;
  status?: string;
}

interface Invoice {
  url: string;
  status: string;
  totalValue: string;
  netValue: string; 
}

interface Estimate {
  id: number;
  reference: string;
  status: string;
  contactUrl: string;   
  currency: string;     
  netValue: string;     
  invoices: Invoice[];
}

interface ProjectItem extends Project {
  expanded?: boolean;
  loadingEstimates?: boolean;
  estimates?: Estimate[];
}

const FreeAgentProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);

  // For the deposit invoice modal:
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [activeEstimate, setActiveEstimate] = useState<Estimate | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);

  // For the final invoice spinner
  const [finalInvoiceLoading, setFinalInvoiceLoading] = useState<{ [estimateId: number]: boolean }>({});

  // For the "Create Estimate" modal
  const [createEstimateModalVisible, setCreateEstimateModalVisible] = useState(false);
  const [activeProjectForEstimate, setActiveProjectForEstimate] = useState<ProjectItem | null>(null);

  // ** NEW: Track if we're loading on the "Create Estimate" call
  const [creatingEstimate, setCreatingEstimate] = useState(false);

  // Basic auth
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const authHeader = `Basic ${btoa(`${username}:${password}`)}`;

  // 1) On mount, load basic project list
  useEffect(() => {
    setLoadingProjects(true);
    setError(null);

    axios
      .get<ProjectItem[]>(`${process.env.REACT_APP_API_URL}/freeagent-projects`, {
        headers: { Authorization: authHeader },
        params: { onlyActive: true, includeCustomerSupportProjects: false },
      })
      .then((res) => {
        setProjects(res.data);
      })
      .catch((err) => {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
      })
      .finally(() => setLoadingProjects(false));
  }, [authHeader]);

  // 2) Load or refresh estimates for a given project
  const handleLoadEstimates = (projectId: number) => {
    setError(null);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, loadingEstimates: true } : p
      )
    );

    axios
      .get<{ id: number; estimates: Estimate[] }>(
        `${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}`,
        { headers: { Authorization: authHeader } }
      )
      .then((res) => {
        const { estimates } = res.data;
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId
              ? { ...p, estimates, expanded: true, loadingEstimates: false }
              : p
          )
        );
      })
      .catch((err) => {
        console.error('Error fetching project details:', err);
        setError('Failed to load project details');
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId ? { ...p, loadingEstimates: false } : p
          )
        );
      });
  };

  // 3) Toggle expand
  const toggleProjectExpand = (projectId: number) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId ? { ...p, expanded: !p.expanded } : p
      )
    );
  };

  // 4) Show deposit modal
  const handleShowDepositModal = (estimate: Estimate, projectId: number) => {
    setActiveEstimate(estimate);
    setActiveProjectId(projectId);
    setDepositModalVisible(true);
  };

  // 5) Called when the deposit form is submitted
  const handleCreateDepositInvoice = async (formData: {
    contact: string;
    amount: number;
  }) => {
    if (!activeEstimate || !activeProjectId) return;

    try {
      const payload = {
        instalmentEstimate: activeEstimate.id,
        contact: formData.contact,
        invoiceItems: [
          {
            description: 'Deposit Payment',
            itemType: 'Services',
            price: formData.amount,
            quantity: 1,
            salesTaxRate: 20,
            salesTaxStatus: 'TAXABLE',
          },
        ],
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/freeagent-invoices`,
        payload,
        {
          headers: { Authorization: authHeader },
        }
      );

      console.log('Deposit invoice created:', resp.data);
      setDepositModalVisible(false);

      handleLoadEstimates(activeProjectId);
    } catch (err) {
      console.error('Error creating deposit invoice:', err);
      setError('Could not create deposit invoice.');
    }
  };

  // 6) Create final invoice immediately
  const createFinalInvoice = (estimateId: number, projectId: number) => {
    setError(null);
    setFinalInvoiceLoading((prev) => ({ ...prev, [estimateId]: true }));

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/freeagent-invoices`,
        {},
        {
          headers: { Authorization: authHeader },
          params: { estimateId },
        }
      )
      .then((resp) => {
        console.log('Final invoice created:', resp.data);
        handleLoadEstimates(projectId);
      })
      .catch((err) => {
        console.error('Error creating final invoice:', err);
        setError('Could not create final invoice');
      })
      .finally(() => {
        setFinalInvoiceLoading((prev) => ({ ...prev, [estimateId]: false }));
      });
  };

  // 7) Show the "Create Estimate" modal
  const handleShowCreateEstimateModal = (project: ProjectItem) => {
    setActiveProjectForEstimate(project);
    setCreateEstimateModalVisible(true);
  };

  // 8) Submit new estimate
  const handleCreateEstimate = async (formData: {
    netValue: string;
    datedOn: string;
    status: string;
  }) => {
    if (!activeProjectForEstimate) return;

    // Turn on the spinner for "Create Estimate"
    setCreatingEstimate(true);

    try {
      const projectUrl = activeProjectForEstimate.url
        ? activeProjectForEstimate.url
        : `https://api.freeagent.com/v2/projects/${activeProjectForEstimate.id}`;

      const contactUrl = activeProjectForEstimate.contactUrl || '';

      const payload = {
        contactUrl,
        project: projectUrl,
        status: formData.status,
        netValue: formData.netValue,
        datedOn: formData.datedOn,
        // If you want to pass reference or other fields, do so here
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/freeagent-estimates`,
        payload,
        { headers: { Authorization: authHeader } }
      );
      console.log('Estimate created:', resp.data);

      // Hide modal
      setCreateEstimateModalVisible(false);

      // Re-fetch the estimates so we see the newly created estimate
      handleLoadEstimates(activeProjectForEstimate.id);
    } catch (err) {
      console.error('Error creating estimate:', err);
      setError('Could not create new estimate.');
    } finally {
      // Turn off spinner
      setCreatingEstimate(false);
    }
  };

  return (
    <div className="container mt-3">
      <h1>FreeAgent Projects</h1>
      {error && <Alert variant="danger">{error}</Alert>}

      {loadingProjects && (
        <div className="text-center my-3">
          <Spinner animation="border" />
        </div>
      )}

      {!loadingProjects && projects.length === 0 && <p>No projects found.</p>}

      {/* Display projects */}
      {!loadingProjects &&
        projects.map((project) => (
          <div className="card mb-3" key={project.id}>
            <div className="card-body">
              <h5 className="card-title d-flex justify-content-between">
                <span>{project.name}</span>
                {project.estimates && (
                  <button
                    className="btn btn-link btn-sm"
                    onClick={() => toggleProjectExpand(project.id)}
                  >
                    {project.expanded ? 'Hide' : 'Show'}
                  </button>
                )}
              </h5>

              {/* Create Estimate button */}
              <button
                className="btn btn-sm btn-secondary me-2"
                onClick={() => handleShowCreateEstimateModal(project)}
              >
                Create Estimate
              </button>

              <button
                className="btn btn-sm btn-primary"
                disabled={project.loadingEstimates}
                onClick={() => handleLoadEstimates(project.id)}
              >
                {project.loadingEstimates ? 'Loading...' : 'Load/Refresh Estimates'}
              </button>

              {/* If estimates are loaded & expanded, show them */}
              {project.estimates && project.expanded && (
                <div className="mt-3">
                  {project.loadingEstimates ? (
                    <Spinner animation="border" />
                  ) : project.estimates.length > 0 ? (
                    <ul>
                      {project.estimates.map((est) => {
                        const estimateNetVal = parseFloat(est.netValue || '0');
                        const sumOfInvoiceNetVals = est.invoices.reduce((acc, inv) => {
                          const invVal = inv.netValue ? parseFloat(inv.netValue) : 0;
                          return acc + invVal;
                        }, 0);

                        const isFullyInvoiced = sumOfInvoiceNetVals >= estimateNetVal;
                        const isLoadingFinalInvoice = !!finalInvoiceLoading[est.id];

                        return (
                          <li key={est.id} style={{ marginBottom: '1rem' }}>
                            <strong>Estimate {est.reference}</strong> ({est.status})
                            {isFullyInvoiced ? ' FULLY INVOICED' : null}
                            <br />

                            <button
                              className="btn btn-sm btn-warning me-2"
                              onClick={() => handleShowDepositModal(est, project.id)}
                              disabled={isFullyInvoiced}
                            >
                              Deposit Invoice
                            </button>

                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => createFinalInvoice(est.id, project.id)}
                              disabled={isFullyInvoiced || isLoadingFinalInvoice}
                            >
                              {isLoadingFinalInvoice ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    style={{ marginRight: '5px', verticalAlign: 'middle' }}
                                  />
                                  Creating...
                                </>
                              ) : (
                                'Final Invoice'
                              )}
                            </button>

                            {/* Show existing invoices */}
                            {est.invoices?.length > 0 && (
                              <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                                <p>Invoices:</p>
                                <ul>
                                  {est.invoices.map((inv, i) => (
                                    <li key={i}>
                                      Status: {inv.status} — Total: {inv.totalValue}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>No estimates found.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

      {/* Deposit Invoice Modal */}
      {activeEstimate && (
        <DepositInvoiceModal
          show={depositModalVisible}
          onHide={() => setDepositModalVisible(false)}
          defaultContactUrl={activeEstimate.contactUrl || ''}
          defaultCurrency={activeEstimate.currency || 'GBP'}
          onSubmit={handleCreateDepositInvoice}
        />
      )}

      {/* Create Estimate Modal */}
      <CreateEstimateModal
        show={createEstimateModalVisible}
        onHide={() => setCreateEstimateModalVisible(false)}
        onSubmit={handleCreateEstimate}
        loading={creatingEstimate}  // pass the loading state here
      />
    </div>
  );
};

export default FreeAgentProjectsPage;
